import Project, { ProjectWithConsultantNames } from "@/entity/Project";
import { FilterBy, SortBy, TableQuery } from "@/components/data-table/types";
import { ListResponseEntity } from "@/services/types";
import FilteringService from "@/services/FilteringService";
import jslinq from "jslinq";
import { DEFAULT_INITIAL_PAGE, DEFAULT_ITEMS_PER_PAGE } from "@/constants";

export default class ProjectFilteringService {
  private static filter(projects: Project[], filter: FilterBy): Project[] {
    return projects.filter((project) => {
      if (
        "id" in filter &&
        !FilteringService.filterByInclude(
          (project.id as number).toString(),
          filter.id
        )
      ) {
        return false;
      }

      if (
        "company.name" in filter &&
        !FilteringService.filterByInclude(
          project.company.name,
          filter["company.name"]
        )
      ) {
        return false;
      }

      if (
        "contact.id" in filter &&
        !FilteringService.filterByEqual(
          (project.contact?.id as number).toString(),
          filter["contact.id"]
        )
      ) {
        return false;
      }

      if (
        "consultantId" in filter &&
        !FilteringService.filterByEqual(
          (project.consultantId ?? 0).toString(),
          filter["consultantId"]
        )
      ) {
        return false;
      }

      if (
        "consultantSecondId" in filter &&
        !FilteringService.filterByEqual(
          (project.consultantSecondId ?? 0).toString(),
          filter["consultantSecondId"]
        )
      ) {
        return false;
      }

      if (
        "status" in filter &&
        !FilteringService.filterByEqual(project.status, filter["status"])
      ) {
        return false;
      }

      if (
        "company.regionId" in filter &&
        !FilteringService.filterByEqual(
          project.company.regionId?.toString() ?? "",
          filter["company.regionId"]
        )
      ) {
        return false;
      }

      if (
        "salesPricePublished" in filter &&
        !FilteringService.filterByRange(
          project.salesPricePublished ?? 0,
          filter["salesPricePublished"]
        )
      ) {
        return false;
      }

      if (
        "company.contactBranchIds" in filter &&
        !FilteringService.filterByBranches(
          filter["company.contactBranchIds"],
          project.company.contactBranches
        )
      ) {
        return false;
      }

      if (
        "company.subBranchIds" in filter &&
        !FilteringService.filterByBranches(
          filter["company.subBranchIds"],
          project.company.subBranches
        )
      ) {
        return false;
      }

      if (
        "projectType" in filter &&
        !FilteringService.filterByEqual(
          project.projectType,
          filter["projectType"]
        )
      ) {
        return false;
      }

      if (
        "valuationDate" in filter &&
        !FilteringService.filterByDate(
          project.valuationDate ?? null,
          filter["valuationDate"]
        )
      ) {
        return false;
      }

      if (
        "valuationReason" in filter &&
        !FilteringService.filterByEqual(
          project.valuationReason ?? "",
          filter["valuationReason"]
        )
      ) {
        return false;
      }

      if (
        "projectRating" in filter &&
        !FilteringService.filterByRange(
          parseFloat(project.projectRating?.toFixed(1) ?? "0"),
          filter["projectRating"]
        )
      ) {
        return false;
      }

      if (
        "creationDate" in filter &&
        !FilteringService.filterByDate(
          project.creationDate ?? null,
          filter["creationDate"]
        )
      ) {
        return false;
      }

      return true;
    });
  }

  private static sort(projects: Project[], sort: SortBy): Project[] {
    let query = jslinq(projects);

    if ("id" in sort) {
      // @ts-ignore
      query = query[FilteringService.orderingMethodName(sort["id"])](
        (el: Project) => {
          return el.id;
        }
      );
    }
    if ("company.name" in sort) {
      // @ts-ignore
      query = query[FilteringService.orderingMethodName(sort["company.name"])](
        (el: Project) => {
          return el?.company?.name?.trim().toLowerCase() ?? "";
        }
      );
    }

    if ("contact.name" in sort) {
      // @ts-ignore
      query = query[FilteringService.orderingMethodName(sort["contact.name"])](
        (el: Project) => {
          if (!el?.contact) {
            return "";
          }

          return (
            el?.contact.firstName.trim().toLowerCase() ??
            "" + " " + el?.contact.lastName.trim().toLowerCase() ??
            ""
          );
        }
      );
    }

    if ("status" in sort) {
      // @ts-ignore
      query = query[FilteringService.orderingMethodName(sort["status"])](
        (el: Project) => {
          return el.status;
        }
      );
    }

    if ("company.region.name" in sort) {
      // @ts-ignore
      query = query[
        FilteringService.orderingMethodName(sort["company.region.name"])
      ]((el: Project) => {
        return el.company.region?.name.trim().toLocaleLowerCase() ?? "";
      });
    }

    if ("consultantId" in sort) {
      // @ts-ignore
      query = query[FilteringService.orderingMethodName(sort["consultantId"])](
        (el: ProjectWithConsultantNames) => {
          return el.consultantName.trim().toLocaleLowerCase() ?? "";
        }
      );
    }

    if ("consultantSecondId" in sort) {
      // @ts-ignore
      query = query[
        FilteringService.orderingMethodName(sort["consultantSecondId"])
      ]((el: ProjectWithConsultantNames) => {
        return el.consultantSecondName.trim().toLocaleLowerCase() ?? "";
      });
    }

    if ("salesPricePublished" in sort) {
      // @ts-ignore
      query = query[
        FilteringService.orderingMethodName(sort["salesPricePublished"])
      ]((el: Project) => {
        return el.salesPricePublished;
      });
    }

    if ("company.contactBranchIds" in sort) {
      // @ts-ignore
      query = query[
        FilteringService.orderingMethodName(sort["company.contactBranchIds"])
      ]((el: Project) => {
        return el.company?.contactBranches
          ? el.company?.contactBranches[0].name.trim().toLowerCase()
          : "";
      });
    }

    if ("company.subBranchIds" in sort) {
      // @ts-ignore
      query = query[
        FilteringService.orderingMethodName(sort["company.subBranchIds"])
      ]((el: Project) => {
        return el.company?.subBranches
          ? el.company?.subBranches[0].name.trim().toLowerCase()
          : "";
      });
    }

    if ("valuationReason" in sort) {
      // @ts-ignore
      query = query[
        FilteringService.orderingMethodName(sort["valuationReason"])
      ]((el: Project) => {
        return el.valuationReason;
      });
    }

    if ("creationDate" in sort) {
      // @ts-ignore
      query = query[FilteringService.orderingMethodName(sort["creationDate"])](
        (el: Project) => {
          return el.creationDate;
        }
      );
    }

    if ("valuationDate" in sort) {
      // @ts-ignore
      query = query[FilteringService.orderingMethodName(sort["valuationDate"])](
        (el: Project) => {
          return el.valuationDate;
        }
      );
    }
    if ("projectRating" in sort) {
      // @ts-ignore
      query = query[FilteringService.orderingMethodName(sort["projectRating"])](
        (el: Project) => {
          return el.projectRating;
        }
      );
    }

    if (query.toList) {
      return query.toList();
    } else {
      return [];
    }
  }

  /**
   * Sorts branches by name in project
   */
  static sortBranchesPerProject(project: Project): Project {
    const sortedBranches =
      project.company?.contactBranches?.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      }) ?? null;

    return {
      ...project,
      company: {
        ...project.company,
        contactBranches: sortedBranches,
      },
    };
  }

  static do(
    projects: Project[],
    query: TableQuery
  ): ListResponseEntity<Project> {
    const {
      page = DEFAULT_INITIAL_PAGE,
      itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
      filterBy = {},
      sortBy = {},
    } = query;

    const filteredProjects = ProjectFilteringService.filter(projects, filterBy);
    const sortedProjects = ProjectFilteringService.sort(
      filteredProjects,
      sortBy
    );
    const paginatedProjects = FilteringService.paginate(
      sortedProjects,
      page,
      itemsPerPage
    );

    return {
      content: paginatedProjects,
      page: page,
      itemsPerPage: itemsPerPage,
      totalItems: filteredProjects.length,
      totalPages: Math.abs(Math.ceil(filteredProjects.length / itemsPerPage)),
    };
  }
}
